import axios from 'axios';
import { setColourwayStamp } from './colourwayPaint';
import { appliedReading, applyMaterials, markApplying, type PartPaint } from './materialsRun';
import { writtenParts } from './openaiBlenderApi';
import { getParts } from './partsRun';
import { partPaints } from './partMaterialRun';
import { readCatalogItems } from './catalogItems';
import { partCatalogId } from './partIdentity';
import { ensureCategories } from './catalogCategories';
import { bumpProductRuns } from './productRuns';
import type { MaterialPair } from './variantLabel';

// A PART'S MATERIAL, FOLLOWED ONTO THE WHOLE PRODUCT'S MESHES.
//
// A written product is built with one material slot per material and each piece put on one of
// them, off what Phase 2 said at the time. Changing a part's material afterwards changed the
// table and nothing else: the backrest went on being drawn in wood after the table said fabric.
// So a change sends every component's material, by the name the build was told (writtenParts,
// the same list the prompt is built from), and the server moves the pieces that disagree — see
// glb.reassigned. Then the materials reading this colourway wears, if one was applied, goes back
// on, which is what gives a moved piece its new material's surface rather than a flat slot.
export async function followPartMaterials(
  docId: string, submodel: string, picks: Record<number, MaterialPair | null> | undefined,
  // Which parts' materials just changed, by index — their OWN meshes are repainted too. Every
  // component when absent: a run answered them all.
  changed?: number[],
): Promise<void> {
  if (!submodel) return;
  const items = getParts(docId);
  // Loaded before partPaints asks which parts are hardware.
  await ensureCategories();
  const pieces = await piecesOf(docId, picks);
  // THE PART'S OWN MESHES, by catalog item — the same pairs the materials panel's apply sends.
  // Only with a reading applied: that is where a material's numbers are, and a part repainted
  // without them would be a guess.
  const reading = appliedReading(docId, submodel);
  const catalog = reading ? await readCatalogItems() : [];
  // And the hardware on a product material (see partPaints).
  const parts: PartPaint[] = reading
    ? partPaints(items, picks, (i) => partCatalogId(catalog, true, items[i]), changed)
    : [];
  if (!pieces.length && !parts.length) return;
  // SPINNERS ON THE MESHES FOR THE WHOLE OF IT, both halves: the move and the re-apply. What is
  // on screen until then is the part in its old material, and nothing said a new one was coming.
  markApplying(docId, submodel, true, parts.map((p) => p.itemId));
  try {
    const moved = await postPieces(docId, submodel, pieces);
    if (reading) await applyMaterials(docId, submodel, reading, parts);
    else if (moved) bumpProductRuns(docId);
  } catch (e) {
    // The table has the answer either way; the mesh catches up on the next change or apply.
    console.warn('[notice] could not follow the part materials onto the meshes', e);
  } finally {
    markApplying(docId, submodel, false);
  }
}

// EVERY COMPONENT'S MATERIAL, by the name the build was told (writtenParts, the same list the
// prompt is built from).
async function piecesOf(docId: string, picks: Record<number, MaterialPair | null> | undefined) {
  const categories = await ensureCategories();
  return (writtenParts(getParts(docId), picks, categories) ?? [])
    .filter((p): p is typeof p & { material: string } => !!p.material)
    .map((p) => ({ name: p.name, material: p.material }));
}

// Which slot each piece wears in this colourway, stored as its paint (see glb.reassigned, which the
// server runs when it paints the shape). Whether anything was sent.
async function postPieces(
  docId: string, submodel: string, pieces: { name: string; material: string }[],
): Promise<boolean> {
  if (!pieces.length) return false;
  const { data } = await axios.post<{ stamp: number }>(
    '/api/notice/3d/materials/pieces', { docId, submodel, pieces },
  );
  setColourwayStamp(submodel, data.stamp);
  return true;
}

// THE PIECES MOVED, AND NOTHING REPAINTED — the first half of applying a reading whose parts
// changed, the apply itself being the repaint (see applyMaterials' `first`).
export async function movePieces(
  docId: string, submodel: string, picks: Record<number, MaterialPair | null>,
): Promise<void> {
  if (!submodel) return;
  await postPieces(docId, submodel, await piecesOf(docId, picks));
}
